import React from "react";
import {
    eRoleIds,
    UserClaimsService,
} from "../../../../services/currentUserService/currentUserService";
import {
    useDeleteRaterByIdMutation,
    useReSendRaterInvitationMutation,
    useReSendRaterRemindersMutation,
    useUpdateRaterSwitchMutation,
    useAddRatersMutation,
} from "../../../../services/cclTokenedEnterpriseRaterApi";
import {
    AddRaterRequest,
    DeleteRaterRequest,
    EmailRequest,
    EmailType,
    RaterSwitchRequest,
} from "../../../../services/types/assessmentAPITypes";
import { Alert, AlertColor, Box, Stack } from "@mui/material";
import RaterManagementDataGridPro from "./raterManagementDataGridPro";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import {
    AccessEventRegistrationAssessmentDetails,
    AccessEventRegistrationDetails,
} from "../../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import EditRaterTypesDrawer from "./editRaterTypesDrawer";
import AddRaterDrawer from "./addRaterDrawer";
import {
    AssessmentsReopenRequestItem,
    Participant,
    ParticipantRaterDetail,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import {
    useGetCclParticipantsByEventKeyQuery,
    useLazyGetParticipantsRatersQuery,
    useLazyGetSessionRatersQuery,
    useReopenAssessmentsMutation,
} from "../../../../services/cclTokenedEnterpriseParticipantApi";

interface RaterManagementPanelProps {
    sessionKey: number;
    programCode: string;
    registration?: Participant;
    readOnly?: boolean;
    updateHidePlatformDates?: (value: boolean) => void;
    hideRaterManagement?: boolean;
    hostPlatformKey?: number;
    surveyPlatformKey?: number;
}

const RaterManagementPanel: React.FC<RaterManagementPanelProps> = (props) => {
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [showAddRaters, setShowAddRaters] = React.useState<boolean>(false);
    const [showRaterTypeDialog, setShowRaterTypeDialog] = React.useState<boolean>(false);
    const [selectedRaterDetail, setSelectedRaterDetail] =
        React.useState<ParticipantRaterDetail | null>(null);
    const [loadingButtonId, setLoadingButtonId] = React.useState<string>("");
    const [raterDetailsList, setRaterDetailsList] = React.useState<ParticipantRaterDetail[]>([]);

    const [
        searchSessionRaters,
        { data: sessionRaterDetailsList, isLoading: sessionRatersIsLoading },
    ] = useLazyGetSessionRatersQuery();
    const [
        searchParticipantRaters,
        { data: participantRaterDetailsList, isLoading: participantRatersIsLoading },
    ] = useLazyGetParticipantsRatersQuery();

    const [reopenAssessments] = useReopenAssessmentsMutation();
    const { data: sessionParticipants } = useGetCclParticipantsByEventKeyQuery(
        props.sessionKey.toString(),
        {
            skip: props.registration !== undefined,
        },
    );
    const [updateRaterType] = useUpdateRaterSwitchMutation();
    const [deleteRater] = useDeleteRaterByIdMutation();
    const [reSendReminders] = useReSendRaterRemindersMutation();
    const [reSendInvitation] = useReSendRaterInvitationMutation();
    const [addRater, { isLoading: addInProgress }] = useAddRatersMutation();

    const { logEvent } = useLogAccessEvent();
    const claimsService = new UserClaimsService();
    const isUserAssessmentManager = claimsService.IsUserInAppRole(eRoleIds.eAssessmentManager);

    React.useEffect(() => {
        if (props.registration !== undefined) {
            searchParticipantRaters(props.registration.esiKey);
        } else {
            searchSessionRaters(props.sessionKey);
        }
        //eslint-disable-next-line
    }, [props.sessionKey, props.registration]);

    React.useEffect(() => {
        if (sessionRaterDetailsList != null) {
            setRaterDetailsList(sessionRaterDetailsList);
        } else if (participantRaterDetailsList != null) {
            setRaterDetailsList(participantRaterDetailsList);
        } else {
            setRaterDetailsList([]);
        }
    }, [sessionRaterDetailsList, participantRaterDetailsList]);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const handleButtonPress = (buttonName: string, ids: number[]) => {
        switch (buttonName) {
            case "add":
                setShowAddRaters(true);
                break;
            case "edit":
                if (ids != null && ids.length === 1 && raterDetailsList != null) {
                    const raterDetail = raterDetailsList.find((r) => r.rmKey === ids[0]);
                    if (raterDetail && raterDetail.instrumentTypeKey === 2) {
                        setSelectedRaterDetail(raterDetail);
                        setShowRaterTypeDialog(true);
                        return;
                    }
                }
                ShowSnackbarElement("Update Rater Not Possible", "error");
                break;
            case "delete":
                if (ids != null && ids.length === 1 && raterDetailsList != null) {
                    handleDelete(ids[0]);
                    return;
                }
                ShowSnackbarElement("Delete Rater Not Possible", "error");
                break;
            case "invitation":
                handleResendInvitations(ids);
                break;
            case "reminder":
                handleResendReminders(ids);
                break;
            case "reopenraterassessments":
                if (ids == null || ids.length === 0 || raterDetailsList == null) return;
                setLoadingButtonId(buttonName);
                const raterDetails = raterDetailsList.filter((r) => ids.includes(r.rmKey));
                if (raterDetails.length > 0) {
                    // converting to set then array removes duplicates
                    const esiKeys = Array.from(
                        new Set(raterDetails.map((r) => +r.participantEsiKey)),
                    );
                    const fullPayload: AssessmentsReopenRequestItem[] = [];
                    for (let i = 0; i < esiKeys.length; i++) {
                        const payload: AssessmentsReopenRequestItem = {
                            esiKey: esiKeys[i],
                            formActualKeys: raterDetails
                                .filter(
                                    (r) =>
                                        +r.participantEsiKey === esiKeys[i] &&
                                        r.formActualKey != null,
                                )
                                .map((r) => r.formActualKey),
                        };
                        fullPayload.push(payload);
                    }
                    reopenAssessments({ requestItems: fullPayload })
                        .unwrap()
                        .then(() => {
                            for (let i = 0; i < raterDetails.length; i++) {
                                const reopenEvtData: AccessEventRegistrationAssessmentDetails = {
                                    esiKey: props.registration?.esiKey.toString() ?? "",
                                    imKey: raterDetails[i].raterImKey.toString() ?? "",
                                    email: raterDetails[i].raterEmail ?? "",
                                    instrument: raterDetails[i].formMasterName ?? "",
                                };
                                logEvent("RaterAssessmentReopened", reopenEvtData);
                            }
                            ShowSnackbarElement("Assessment(s) Re-Opened", "success");
                            setLoadingButtonId("");
                            // refresh data, order is important here because we get session key even if we do get registration prop
                            if (props.registration?.esiKey != null) {
                                searchParticipantRaters(props.registration.esiKey);
                            } else if (props.sessionKey != null) {
                                searchSessionRaters(props.sessionKey);
                            }
                        })
                        .catch(() => {
                            ShowSnackbarElement("Re-Open Assessment(s) Failed", "error");
                            setLoadingButtonId("");
                        });
                }
                break;
        }
    };

    const logEvents = (event: string, paxIds: number[]): void => {
        let evtDataList: AccessEventRegistrationDetails[] = [];
        if (props.registration === undefined) {
            if (sessionParticipants != null) {
                const paxForLogging = sessionParticipants?.filter((p) => paxIds.includes(p.esiKey));

                evtDataList = paxForLogging.map((p) => {
                    return {
                        esiKey: p.esiKey.toString(),
                        imKey: p.imKey.toString(),
                        email: p.emailAddress,
                    } as AccessEventRegistrationDetails;
                });
            }
        } else {
            evtDataList.push({
                esiKey: props.registration.esiKey.toString(),
                imKey: props.registration.imKey.toString(),
                email: props.registration.emailAddress,
            });
        }

        if (evtDataList.length > 0) {
            evtDataList.forEach((ld) => {
                logEvent(event, ld);
            });
        }
    };

    const handleResendReminders = (ids: number[]) => {
        if (
            raterDetailsList == null ||
            raterDetailsList.length <= 0 ||
            ids == null ||
            ids.length <= 0
        ) {
            ShowSnackbarElement("Reminder Resend Not Possible", "error");
            return;
        }
        setLoadingButtonId("reminder");
        var newEmailrequest: EmailRequest[] = [];
        ids.forEach((i) => {
            const EmailList: EmailRequest = {
                EmailDateToSend: new Date(),
                EmailTypeId: EmailType.RaterReminder,
                ParticipantId:
                    raterDetailsList?.filter((r) => r.rmKey === i)[0].participantEsiKey ?? "",
                RaterId: parseInt(i.toString()),
            };
            newEmailrequest.push(EmailList);
        });
        reSendReminders(newEmailrequest)
            .unwrap()
            .then(() => {
                logEvents(
                    "RaterRemindersResent",
                    Array.from(new Set(newEmailrequest.map((email) => +email.ParticipantId))),
                );

                ShowSnackbarElement("Reminder(s) Resent", "success");
                setLoadingButtonId("");
            })
            .catch(() => {
                ShowSnackbarElement("Reminder Resend Failed", "error");
                setLoadingButtonId("");
            });
    };

    const handleResendInvitations = (ids: number[]) => {
        if (
            raterDetailsList == null ||
            raterDetailsList.length <= 0 ||
            ids == null ||
            ids.length <= 0
        ) {
            ShowSnackbarElement("Invitation Resend Not Possible", "error");
            return;
        }
        setLoadingButtonId("invitation");
        var newEmailrequest: EmailRequest[] = [];
        ids.forEach((i) => {
            const EmailList: EmailRequest = {
                EmailDateToSend: new Date(),
                EmailTypeId: EmailType.ProductRaterInvitation,
                ParticipantId:
                    raterDetailsList?.filter((r) => r.rmKey === i)[0].participantEsiKey ?? "",
                RaterId: i,
            };
            newEmailrequest.push(EmailList);
        });
        reSendInvitation(newEmailrequest)
            .unwrap()
            .then(() => {
                logEvents(
                    "RaterInvitationsResent",
                    Array.from(new Set(newEmailrequest.map((email) => +email.ParticipantId))),
                );

                ShowSnackbarElement("Invitation(s) Resent", "success");
                setLoadingButtonId("");
            })
            .catch(() => {
                ShowSnackbarElement("Invitation Resend Failed", "error");
                setLoadingButtonId("");
            });
    };

    const handleEditRater = async (RMKey: number, RaterTypeKey: number) => {
        const raterObj = raterDetailsList?.filter((r) => r.rmKey === RMKey)[0];
        if (raterObj.instrumentTypeKey !== 2) return;
        const request: RaterSwitchRequest = {
            formActualID: raterObj?.formActualKey ?? 0,
            raterTypeKey: RaterTypeKey,
            rmkey: RMKey,
            esiKey: raterObj?.participantEsiKey ? +raterObj?.participantEsiKey : 0,
            esKey: props.sessionKey,
        };
        try {
            await updateRaterType(request).unwrap();
            logEvents("RaterTypeChanged", [request.esiKey]);

            ShowSnackbarElement("Rater Updated", "success");
            setLoadingButtonId("");
            updateRaters();
            setShowRaterTypeDialog(false);
        } catch {
            ShowSnackbarElement("Rater Update Failed", "error");
            setLoadingButtonId("");
            setShowRaterTypeDialog(false);
        }
    };

    const handleDelete = (RMKey: number) => {
        setLoadingButtonId("delete");
        const raterObj = raterDetailsList?.filter((r) => r.rmKey === RMKey)[0];
        if (raterObj.instrumentTypeKey !== 2) return;
        const request: DeleteRaterRequest = {
            rmkey: RMKey,
            esiKey: raterObj?.participantEsiKey ? +raterObj?.participantEsiKey : 0,
            esKey: props.sessionKey,
        };
        deleteRater(request)
            .unwrap()
            .then(() => {
                logEvents("RaterDeleted", [request.esiKey]);

                ShowSnackbarElement("Rater Deleted", "success");
                setLoadingButtonId("");
                updateRaters();
            })
            .catch(() => {
                ShowSnackbarElement("Rater Deletion Failed", "error");
                setLoadingButtonId("");
            });
    };

    const handleAddRater = (request: AddRaterRequest) => {
        var addRatersList: AddRaterRequest[] = [];
        addRatersList.push(request);
        addRater(addRatersList)
            .unwrap()
            .then(() => {
                logEvents(
                    "RaterAdded",
                    Array.from(new Set(addRatersList.map((rq) => rq.participantId))),
                );

                ShowSnackbarElement("Rater Added", "success");
                setShowAddRaters(false);
                updateRaters();
            })
            .catch(() => {
                ShowSnackbarElement("Rater Addition Failed", "error");
            });
    };

    const updateRaters = () => {
        if (props.registration != null) {
            searchParticipantRaters(props.registration.esiKey);
        } else {
            searchSessionRaters(props.sessionKey);
        }
    };

    React.useEffect(() => {
        if (props.hideRaterManagement) {
            props.updateHidePlatformDates?.(true);
        } else {
            props.updateHidePlatformDates?.(false);
        }
    }, [props.hideRaterManagement, props.updateHidePlatformDates]);

    if (props.hideRaterManagement) {
        return (
            <Box width={1}>
                <Alert severity="info">
                    No assessment status information found for this session.
                </Alert>
            </Box>
        );
    }

    return (
        <Stack height={1} width={1}>
            {showAddRaters ? (
                <AddRaterDrawer
                    raterList={raterDetailsList ?? []}
                    esKey={props.sessionKey}
                    open={true}
                    onAdd={handleAddRater}
                    onCancel={() => setShowAddRaters(false)}
                    registration={props.registration}
                    isAdding={addInProgress}
                />
            ) : null}
            {showRaterTypeDialog ? (
                <EditRaterTypesDrawer
                    open={true}
                    onOk={(rMKey, raterTypeId) => handleEditRater(rMKey, raterTypeId)}
                    raterDetail={selectedRaterDetail}
                    onCancel={() => setShowRaterTypeDialog(false)}
                />
            ) : null}
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
            <Box width={1}>
                <RaterManagementDataGridPro
                    ratersDetailsList={raterDetailsList ?? []}
                    programCode={props.programCode}
                    showToolbar={true}
                    isSessionRater={props.registration == null}
                    isUserAssessmentManager={isUserAssessmentManager}
                    readOnly={props.readOnly}
                    isLoading={participantRatersIsLoading || sessionRatersIsLoading}
                    handleButtonClick={handleButtonPress}
                    loadingButtonId={loadingButtonId}
                    isSuperAdmin={claimsService.IsUserSuperAdmin()}
                    hostingPlatformKey={props.hostPlatformKey ?? 0}
                    surveyPlatformKey={props.surveyPlatformKey ?? 0}
                />
            </Box>
        </Stack>
    );
};

export default RaterManagementPanel;
